import { convertDateFormat } from '@features/siteStatusV2/utils/helpers';
import theme from '@theme';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import styled from 'styled-components';

import MapBadgeInformations from './constants/BadgeInformations.map';
import EligibilityBadge from './EligibilityBadge';
import EligibilityModal from './EligibilityModal';
import { SiteInformationsSkeleton } from './skeltons';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

const SiteInformations: React.FC = () => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const { isSiteLoading, site } = useSelector((state: RootState) => state.site);
  const [isModalShown, setIsModalShown] = useState(false);

  if (isSiteLoading) {
    return <SiteInformationsSkeleton />;
  }

  if (site) {
    const details = [
      {
        title: t('buildingDetail.commercialOpenDate', `Commercial Opening Date`),
        value: convertDateFormat(site.commercialOpeningDate)
      },
      {
        title: t('buildingDetail.copperEndDate', `Copper End Date`),
        value: convertDateFormat(site.copperEndDate)
      },
      {
        title: t('buildingDetail.connectionType', `Connection Type`),
        value: site.connectionType
      },
      {
        title: t('buildingDetail.referencepm', `PM reference`),
        value: site.referencepm
      }
    ];

    return (
      <SiteInformationsGrid>
        <SiteInformationsFirstRow>
          <InformationContainer>
            <SubTitleSection>{t('buildingDetail.eligibilityState')}</SubTitleSection>
            <InformationSection>
              <EligibilityBadge
                status={site.eligibilityStatus}
                icon={MapBadgeInformations[site.eligibilityStatus]?.badgeIcon}
                setIsModalShown={setIsModalShown}
              />
              <DisplayInformations>
                {t(MapBadgeInformations[site.eligibilityStatus]?.informations)}
              </DisplayInformations>
            </InformationSection>
          </InformationContainer>
          <EligibilityModal
            isModalShown={isModalShown}
            setIsModalShown={setIsModalShown}
            frozenInformation={{
              freezeDate: convertDateFormat(site.freezeDate),
              unfreezeDate: convertDateFormat(site.unfreezeDate),
              freezeReason: site.freezeReason,
              comments: site.comments,
              pboReference: site.pboReference,
              freezePbo: convertDateFormat(site.freezePbo),
              dateUnfreezePbo: convertDateFormat(site.dateUnfreezePbo),
              freezePboReason: site.freezePboReason
            }}
          />
        </SiteInformationsFirstRow>
        <SiteInformationsSecondRow>
          {details.map((detail, index) => (
            <InformationContainer key={index}>
              <SubTitleSection>{detail.title}:</SubTitleSection>
              <InformationSection>
                <DisplayInformations>{detail.value}</DisplayInformations>
              </InformationSection>
            </InformationContainer>
          ))}
        </SiteInformationsSecondRow>
      </SiteInformationsGrid>
    );
  }
  return null;
};

const SubTitleSection = styled.div`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: ${theme.palette.grey[500]};
  display: flex;
  align-items: center;
  padding-right: 0.625rem;
`;

const InformationSection = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-direction: row;
`;

const DisplayInformations = styled.span`
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: ${theme.palette.grey[400]};
`;

const InformationContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-direction: row;
  height: 100%;
`;

const SiteInformationsGrid = styled.div`
  display: grid;
  row-gap: 0.625rem;
  padding-top: 0.625rem;
`;

const SiteInformationsFirstRow = styled.div`
  grid-column: 1 / -1;
`;

const SiteInformationsSecondRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
`;

export default SiteInformations;
